import { TranslateFunction } from '@griply/types';

// The event endpoints answer a failure with the callable protocol's error envelope, and both Firebase
// SDKs surface its `details` on the thrown error. `code` is the stable token; the `message` beside it is
// English-only, which is why it is never rendered — it exists for Sentry and for the logs.
const KNOWN_CODES = [
  'untranslatable-recurrence',
  'video-call-not-removable',
  'connection-unavailable',
  'connection-cannot-write-events',
  'provider-rejected',
  // the UI gates this before the call, so reaching it means an out-of-date client or a lapsed plan
  'premium-required',
  // Not expressible on either provider; the occurrence prompt steers around it, this is the backstop.
  'occurrence-not-movable',
  // Deleted or moved by hand while the editor was open; only a move can hit it.
  'event-gone',
];

export const getCalendarEventErrorCode = (error: unknown): string | null => {
  const details = (error as { details?: unknown } | null)?.details;
  const code = (details as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : null;
};

const getKnownErrorCode = (error: unknown): string | null => {
  const code = getCalendarEventErrorCode(error);
  return code && KNOWN_CODES.includes(code) ? code : null;
};

// a known code is a per-user state with its own copy, not an incident worth Sentry
export const isKnownCalendarEventErrorCode = (error: unknown): boolean =>
  getKnownErrorCode(error) !== null;

// an unknown code gets the translated generic; the provider's English sentence would read as a bug
export const getCalendarEventErrorMessage = (
  error: unknown,
  t: TranslateFunction,
): string => {
  const code = getKnownErrorCode(error);
  if (!code) {
    return t('forms.event.error.description');
  }

  return t(`forms.event.errors.${code}`);
};
